<template>
  <section class="container">
    <div
      v-if="loading && !objectId"
      class="text-center mt-5">
      <coozzy-spinner />
    </div>
    <template v-else-if="linkInvalid">
      <div class="row mt-4">
        <div class="col">
          <p class="mb-0">
            {{ $t('message.marketingObjects.viewingAppointments.publicForm.invalidLink') }}
          </p>
        </div>
      </div>
    </template>
    <template v-else>
      <div class="row">
        <div class="col pl-3 pr-3">
          <coozzy-page-title
            class="mt-4"
            :wrap="false"
            :title="$tc(pageTitleKey)" />
          <p>
            {{ $t(pageSubtitleKey) }}
          </p>
        </div>
      </div>
      <div
        v-if="objectId"
        class="row">
        <coozzy-card class="col pl-0 pr-0 mb-3 margin15">
          <application-form-object-view
            :default-object-id="objectId"
            :show-apply-button="false" />
        </coozzy-card>
      </div>
      <div class="row mb-4">
        <coozzy-card class="col pl-0 pr-0 mb-3 margin15">
          <div class="pl-3 pr-3 pt-3 pb-3">
            <div
              v-if="loading"
              class="text-center py-3">
              <coozzy-spinner />
            </div>
            <template v-else-if="hasScheduledAppointments">
              <div
                v-for="appointment in contactScheduledAppointments"
                :key="appointment.id"
                class="appointment-entry mb-3">
                <div class="row align-items-center">
                  <div class="col-12 col-md-7">
                    <span class="font-weight-bold">{{ formatAppointmentDate(appointment) }}</span>
                    <span class="ml-2">{{ formatAppointmentTimeRange(appointment) }}</span>
                  </div>
                  <div class="col-12 col-md-5 text-md-right">
                    {{ $t('message.marketingObjects.viewingAppointments.publicForm.participants') }}
                    {{ getParticipantCount(appointment) }}
                  </div>
                </div>
                <div class="row mt-2">
                  <div class="col-12">
                    <coozzy-dropdown
                      design="default"
                      size="sm"
                      :text="$t('message.marketingObjects.viewingAppointments.publicForm.addToCalendar')">
                      <coozzy-dropdown-item
                        @click="openCalendarLink(appointment, 'google')">
                        {{ $t('message.marketingObjects.viewingAppointments.publicForm.calendarGoogle') }}
                      </coozzy-dropdown-item>
                      <coozzy-dropdown-item
                        @click="openCalendarLink(appointment, 'outlook')">
                        {{ $t('message.marketingObjects.viewingAppointments.publicForm.calendarOutlook') }}
                      </coozzy-dropdown-item>
                      <coozzy-dropdown-item
                        @click="openCalendarLink(appointment, 'apple')">
                        {{ $t('message.marketingObjects.viewingAppointments.publicForm.calendarApple') }}
                      </coozzy-dropdown-item>
                      <coozzy-dropdown-item
                        @click="openCalendarLink(appointment, 'ics')">
                        {{ $t('message.marketingObjects.viewingAppointments.publicForm.calendarDownloadIcs') }}
                      </coozzy-dropdown-item>
                    </coozzy-dropdown>
                  </div>
                </div>
              </div>
              <div
                v-if="!showAddForm"
                class="row mt-3">
                <div class="col-12 col-md-auto">
                  <coozzy-button
                    design="default"
                    @click="showAddForm = true">
                    + {{ $t('message.marketingObjects.viewingAppointments.publicForm.addViewingAppointment') }}
                  </coozzy-button>
                </div>
              </div>
            </template>
            <add-viewing-appointments-form
              v-if="!loading && showAddForm"
              class="mt-3"
              :token="token"
              :existing-appointments="scheduledAppointments"
              @saved="onAppointmentsSaved" />
          </div>
        </coozzy-card>
      </div>
    </template>
  </section>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown.vue'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import ApplicationFormObjectView from '@/marketingBasic/components/applicationForm/ApplicationFormObjectView.vue'
import AddViewingAppointmentsForm from '@/marketingBasic/components/viewingAppointments/AddViewingAppointmentsForm.vue'
import CalendarSchedulerApi from '@/misc/apis/CalendarSchedulerApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import { dateUtils } from '@/mixins/dateUtils'
import { mapGetters } from 'vuex'
import {
  SchedulerAppointment,
  SchedulerAppointmentStatus
} from '@/types/api'
import { compareSchedulerAppointmentSlots, formatSchedulerSlotTimeRange, isContactLedAppointment } from '@/misc/utils/viewingAppointmentSlotUtils'
import {
  formatViewingObjectAddress,
  openViewingAppointmentCalendar,
  type ViewingAppointmentCalendarProvider
} from '@/misc/utils/viewingAppointmentCalendar'

export default Vue.extend({
  name: 'AddViewingAppointmentsLandingPage',
  components: {
    CoozzyPageTitle,
    CoozzyCard,
    CoozzyButton,
    CoozzyDropdown,
    CoozzyDropdownItem,
    CoozzySpinner,
    ApplicationFormObjectView,
    AddViewingAppointmentsForm
  },
  mixins: [dateUtils],
  metaInfo() {
    return {
      title: this.$t(this.pageTitleKey)
    }
  },
  props: {
    token: {
      type: String as PropType<string>,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      linkInvalid: false,
      appointments: [] as SchedulerAppointment[],
      objectId: '' as string,
      objectAddress: '' as string,
      showAddForm: false
    }
  },
  computed: {
    ...mapGetters('internationalization', ['getCurrentLanguage']),
    pageTitleKey(): string {
      return this.hasScheduledAppointments
        ? 'message.marketingObjects.viewingAppointments.publicForm.scheduledTitle'
        : 'message.marketingObjects.viewingAppointments.publicForm.title'
    },
    pageSubtitleKey(): string {
      return this.hasScheduledAppointments
        ? 'message.marketingObjects.viewingAppointments.publicForm.scheduledSubtitle'
        : 'message.marketingObjects.viewingAppointments.publicForm.subtitle'
    },
    scheduledAppointments(): SchedulerAppointment[] {
      return this.appointments
        .filter(appointment => appointment.status === SchedulerAppointmentStatus.APPOINTMENT_STATUS_SCHEDULED)
        .sort(compareSchedulerAppointmentSlots)
    },
    contactScheduledAppointments(): SchedulerAppointment[] {
      return this.scheduledAppointments.filter(appointment => isContactLedAppointment(appointment))
    },
    hasScheduledAppointments(): boolean {
      return this.contactScheduledAppointments.length > 0
    },
    formUrl(): string {
      return window.location.href
    },
    calendarEventTitle(): string {
      if (this.objectAddress) {
        return this.$t(
          'message.marketingObjects.viewingAppointments.publicForm.calendarEventTitle',
          { address: this.objectAddress }
        ).toString()
      }
      return this.$t('message.marketingObjects.viewingAppointments.publicForm.calendarEventTitlePlain').toString()
    },
    localeForDate(): string {
      const language = this.getCurrentLanguage || 'de'
      if (language === 'en') {
        return 'en-GB'
      }
      if (language === 'fr') {
        return 'fr-CH'
      }
      if (language === 'it') {
        return 'it-CH'
      }
      return 'de-CH'
    }
  },
  mounted() {
    const userLang = navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage || 'de'
    this.$store.dispatch('internationalization/changeCurrentLanguage', userLang.substring(0, 2))
    this.loadAppointments()
  },
  beforeDestroy() {
    if (!this.$store.getters['user/getAccessToken']) {
      this.$store.dispatch('user/setLogoUrl', null)
    }
  },
  methods: {
    loadAppointments() {
      this.loading = true
      CalendarSchedulerApi.listAppointmentsByToken(this.token)
        .then(response => {
          this.appointments = response.appointments || []
          this.resolveObjectId(response)
          this.showAddForm = !this.hasScheduledAppointments
          this.loading = false
        })
        .catch(e => {
          console.log(e)
          if (e?.response?.status === 404) {
            this.linkInvalid = true
          } else {
            Vue.toasted.show(this.$t('message.loadingErrors.generic'), { type: 'error' })
          }
          this.loading = false
        })
    },
    resolveObjectId(response?: { objectId?: string; appointments?: SchedulerAppointment[] }) {
      const previous = this.objectId
      if (response?.objectId) {
        this.objectId = response.objectId
      } else {
        const appointments = response?.appointments || this.appointments
        const withObjectId = appointments.find(appointment => appointment.objectId)
        if (withObjectId?.objectId) {
          this.objectId = withObjectId.objectId
        }
      }
      if (this.objectId && this.objectId !== previous) {
        this.loadObjectAddress(this.objectId)
      }
    },
    loadObjectAddress(objectId: string) {
      ObjectApi.getMarketingObject(objectId)
        .then((response: { address?: { careOf?: string; street?: string; streetNumber?: string; zip?: string; city?: string } | null }) => {
          this.objectAddress = formatViewingObjectAddress(response?.address)
        })
        .catch((e: unknown) => {
          console.log(e)
        })
    },
    onAppointmentsSaved(newAppointments: SchedulerAppointment[]) {
      if (Array.isArray(newAppointments) && newAppointments.length > 0) {
        const byId = new Map(this.appointments.map(appointment => [appointment.id, appointment]))
        newAppointments.forEach(appointment => {
          byId.set(appointment.id, appointment)
        })
        this.appointments = Array.from(byId.values())
        this.resolveObjectId({ appointments: this.appointments })
      } else {
        this.loadAppointments()
        return
      }
      this.showAddForm = false
    },
    getParticipantCount(appointment: SchedulerAppointment): number {
      return appointment.participants?.length || 0
    },
    formatAppointmentDate(appointment: SchedulerAppointment): string {
      if (!appointment?.date) {
        return ''
      }
      const date = new Date(appointment.date.year, (appointment.date.month || 1) - 1, appointment.date.day || 1)
      return date.toLocaleDateString(this.localeForDate, {
        weekday: 'long',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    formatAppointmentTimeRange(appointment: SchedulerAppointment): string {
      return formatSchedulerSlotTimeRange(appointment, isContactLedAppointment(appointment))
    },
    openCalendarLink(appointment: SchedulerAppointment, provider: ViewingAppointmentCalendarProvider) {
      openViewingAppointmentCalendar(
        {
          appointment,
          title: this.calendarEventTitle,
          location: this.objectAddress,
          url: this.formUrl
        },
        provider
      )
    }
  }
})
</script>

<style lang="scss" scoped>
.margin15 {
  margin: 0 15px;
}
</style>
